import type { WithCssVariables } from '@/lib/css-variables';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { motion, type MotionStyle } from 'motion/react';
import { Button } from '@/components/ui/button';
import { Check, Loader2 } from 'lucide-react';
import { cx } from 'class-variance-authority';
import { ONBOARDING_PLATFORM_OPTIONS } from '@/lib/onboarding-platform-options';
import { usePatchTenantCreationRequest } from '@/hooks/use-patch-tenant-request';
import {
  OnboardingPreviewProvider,
  useOnboardingPreview,
} from '@/pages/onboarding/onboarding-preview-context';
import { IPhoneMockup } from '@/pages/onboarding/mockup/iphone-mockup';
import { markStudioModalTransition } from '@/pages/onboarding/studio-modal-transition';
import { ChooseStoreMockupScene } from '@/pages/onboarding/mockup/scenes/choose-store-mockup-scene';
import { OnboardingStepsIndicator } from '@/pages/onboarding/onboarding-steps-indicator';
import {
  OnboardingPageLayout,
  OnboardingPageHeader,
  OnboardingPageContent,
  OnboardingPageFooter,
} from '@/pages/onboarding/onboarding-page-layout';

// Step 1: platform selection. The TenantCreationRequest itself is created
// (or resumed, applying T4's legacy-TCR detection) at login time, before the
// lead ever reaches this route — TenantCreationRequestProvider (wrapping the
// /onboarding/:tenantCreationRequestSlug subtree) already resolves it from
// the route param, so this page has nothing extra to create on mount.

const PLATFORM_OPTIONS = ONBOARDING_PLATFORM_OPTIONS;

export default function ChooseStorePage() {
  return (
    <OnboardingPreviewProvider>
      <ChooseStorePageContent />
    </OnboardingPreviewProvider>
  );
}

function ChooseStorePageContent() {
  const navigate = useNavigate();
  const patchCreationRequest = usePatchTenantCreationRequest();
  const preview = useOnboardingPreview();

  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(
    preview.storePlatform
  );
  // Separate from `selectedPlatform` on purpose: the phone should always
  // start locked when this step mounts (even if a platform was already
  // chosen in a previous visit), and only unlock on "Continuar" — not just
  // from tapping a platform card — so the unlock reads as the payoff of
  // confirming the choice, not a side-effect of merely selecting one.
  const [hasChosenPlatform, setHasChosenPlatform] = useState(false);

  const onContinue = async () => {
    if (!selectedPlatform) {
      return;
    }

    setHasChosenPlatform(true);
    // Give the unlock animation a moment to actually play before navigating
    // away, instead of racing it against (and likely losing to) the patch
    // request + page transition.
    await Promise.all([
      patchCreationRequest.mutateAsync({ storePlatform: selectedPlatform }),
      new Promise((resolve) => setTimeout(resolve, 500)),
    ]);
    markStudioModalTransition('present');
    navigate('../app-name-link', { viewTransition: true });
  };

  return (
    <OnboardingPageLayout
      // The platform cards must be on the first screen; the Studio joins in
      // step 2, once there is an app to show.
      mobileMockup={false}
      mockup={
        <IPhoneMockup sceneKey="choose-store" chromeBaked>
          <ChooseStoreMockupScene
            unlocked={hasChosenPlatform}
            appName={preview.appName}
            appIconUrl={preview.appIconUrl}
          />
        </IPhoneMockup>
      }
    >
      <OnboardingStepsIndicator currentStep="choose-store" />

      <OnboardingPageHeader
        title="Qual é a plataforma da sua loja?"
        subtitle="Isso nos ajuda a preparar seu app do jeito certo."
      />

      <OnboardingPageContent>
        <div className="grid grid-cols-2 gap-4">
          {PLATFORM_OPTIONS.map((option, index) => {
            const isSelected = selectedPlatform === option.id;
            const cardStyle: WithCssVariables<MotionStyle> = {
              '--brand': option.color,
              backgroundColor: option.cardBackground,
            };

            return (
              <motion.button
                key={option.id}
                type="button"
                onClick={() => setSelectedPlatform(option.id)}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                whileHover={{ y: -4 }}
                whileTap={{ scale: 0.98 }}
                style={cardStyle}
                className={cx(
                  'group relative flex flex-col items-center gap-4 rounded-2xl border-2 p-6 text-center shadow-sm transition-colors',
                  !option.cardBackground && 'bg-card',
                  isSelected
                    ? 'border-[var(--brand)] shadow-md'
                    : 'border-border hover:border-[var(--brand)]/40'
                )}
              >
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                    className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full text-white"
                    style={{ backgroundColor: option.color }}
                  >
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  </motion.div>
                )}

                <img
                  src={option.logo}
                  alt={option.label}
                  className="h-10 max-w-full object-contain transition-transform group-hover:scale-105"
                />
              </motion.button>
            );
          })}
        </div>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Não encontrou sua plataforma?{' '}
          <a
            href={`mailto:${import.meta.env.VITE_PUBLIC_CONTACT_EMAIL}`}
            className="underline underline-offset-2 hover:text-foreground"
          >
            Fale com a gente
          </a>
        </p>
      </OnboardingPageContent>

      <OnboardingPageFooter>
        <Button
          type="button"
          disabled={selectedPlatform === null || patchCreationRequest.isPending}
          className="min-w-[100px]"
          onClick={onContinue}
        >
          {patchCreationRequest.isPending ? (
            <Loader2 className="animate-spin" />
          ) : (
            'Continuar'
          )}
        </Button>
      </OnboardingPageFooter>
    </OnboardingPageLayout>
  );
}
